/**
 * SEO Route Metadata Configuration
 *
 * Defines static metadata defaults for every public-facing route.
 * Dynamic routes (e.g. /articles/:id) override these values after data resolves.
 *
 * Spec reference: seo-003 §2 Affected Pages & Routes
 */

import { getSiteBaseUrl } from "utils/seoCanonical";

export interface RouteMetaConfig {
  title: string;
  description: string;
  ogType: "website" | "article" | "profile";
  ogImage?: string;
  noIndex?: boolean;
}

const SITE_DESC =
  "Engage in constructive political discourse. Join room-based debates, read news feeds, and connect with people nearby.";

export const getDefaultOgImage = (): string =>
  `${getSiteBaseUrl()}/assets/og-default.png`;

/**
 * Static route metadata map.
 * Keys are exact path strings or route prefixes.
 */
export const ROUTE_META: Record<string, RouteMetaConfig> = {
  "/": {
    title: "Constructive Political Discourse",
    description: SITE_DESC,
    ogType: "website",
  },
  "/home": {
    title: "Home",
    description: SITE_DESC,
    ogType: "website",
  },
  "/about": {
    title: "About Us",
    description:
      "How does Moderate Politics work? Learn about our features, dynamic debate rooms, and connecting with local voices.",
    ogType: "website",
  },
  "/articles": {
    title: "Articles & Information",
    description:
      "Read Moderate Politics articles and information on constructive debate, community, and current issues.",
    ogType: "article",
  },
  "/news": {
    title: "News",
    description: "Latest news and updates from Moderate Politics.",
    ogType: "article",
    noIndex: true,
  },
  "/contact": {
    title: "Contact Us",
    description:
      "Get in touch with the Moderate Politics team for support, feedback, or partnership inquiries.",
    ogType: "website",
  },
  "/terms": {
    title: "Terms & Conditions",
    description:
      "Read the Moderate Politics Terms and Conditions governing your use of the platform.",
    ogType: "website",
  },
  "/termandservices": {
    title: "Terms & Conditions",
    description:
      "Read the Moderate Politics Terms and Conditions governing your use of the platform.",
    ogType: "website",
  },
  "/terms-and-conditions": {
    title: "Terms & Conditions",
    description:
      "Read the Moderate Politics Terms and Conditions governing your use of the platform.",
    ogType: "website",
    noIndex: true,
  },
  "/rules": {
    title: "Community Rules",
    description:
      "Understand the Moderate Politics community guidelines and rules for constructive debate.",
    ogType: "website",
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description:
      "Moderate Politics Privacy Policy: how we collect, use, and protect your personal data.",
    ogType: "website",
    noIndex: true,
  },
  "/conversations-nearby": {
    title: "Conversations Nearby",
    description:
      "Discover active public debates, political rooms, and local discussions in your area.",
    ogType: "website",
  },
  "/profile": {
    title: "User Profile",
    description: "View user public profile on Moderate Politics.",
    ogType: "profile",
  },
  "/profile/edit": {
    title: "Edit Profile",
    description: "Update your Moderate Politics profile.",
    ogType: "website",
    noIndex: true,
  },
  "/login": {
    title: "Login",
    description: "Sign in to your Moderate Politics account.",
    ogType: "website",
    noIndex: true,
  },
  "/registration": {
    title: "Create Account",
    description:
      "Join Moderate Politics – a platform for constructive political discourse.",
    ogType: "website",
    noIndex: true,
  },
  "/forgot-password": {
    title: "Forgot Password",
    description: "Reset your Moderate Politics account password.",
    ogType: "website",
    noIndex: true,
  },
  "/reset-password": {
    title: "Reset Password",
    description: "Choose a new password for your Moderate Politics account.",
    ogType: "website",
    noIndex: true,
  },
  "/restorePassword": {
    title: "Restore Password",
    description: "Restore access to your Moderate Politics account.",
    ogType: "website",
    noIndex: true,
  },
  "/restore-password": {
    title: "Restore Password",
    description: "Restore access to your Moderate Politics account.",
    ogType: "website",
    noIndex: true,
  },
  "/dashboard": {
    title: "Dashboard",
    description: SITE_DESC,
    ogType: "website",
    noIndex: true,
  },
  "/chat-room": {
    title: "Chat Room",
    description: SITE_DESC,
    ogType: "website",
    noIndex: true,
  },
  "/call": {
    title: "Call",
    description: SITE_DESC,
    ogType: "website",
    noIndex: true,
  },
  "/jitsi": {
    title: "Video Call",
    description: SITE_DESC,
    ogType: "website",
    noIndex: true,
  },
  "/help": {
    title: "Help",
    description: "Help and settings for Moderate Politics.",
    ogType: "website",
    noIndex: true,
  },
  "/subscription": {
    title: "Subscription",
    description: "Manage your Moderate Politics subscription.",
    ogType: "website",
    noIndex: true,
  },
  "/invite-friend": {
    title: "Invite a Friend",
    description: "Invite friends to Moderate Politics.",
    ogType: "website",
    noIndex: true,
  },
  "/currency": {
    title: "Currency",
    description: "Gold piece and currency settings.",
    ogType: "website",
    noIndex: true,
  },
  "/date-and-time": {
    title: "Date and Time",
    description: "Date and time display settings.",
    ogType: "website",
    noIndex: true,
  },
  "/notifications": {
    title: "Notifications",
    description: "Notification settings.",
    ogType: "website",
    noIndex: true,
  },
  "/email-notifications": {
    title: "Email Notifications",
    description: "Email notification settings.",
    ogType: "website",
    noIndex: true,
  },
  "/feedback": {
    title: "Feedback",
    description: "Send feedback to Moderate Politics.",
    ogType: "website",
    noIndex: true,
  },
  "/explainer-video": {
    title: "Explainer Video",
    description: "Watch the Moderate Politics explainer video.",
    ogType: "website",
    noIndex: true,
  },
};

const FALLBACK_META: RouteMetaConfig = {
  title: "Moderate Politics",
  description: SITE_DESC,
  ogType: "website",
  noIndex: true,
};

/**
 * Resolve the closest matching metadata for a given pathname.
 * Unknown routes default to noindex so private app pages are not advertised.
 */
export const resolveRouteMeta = (pathname: string): RouteMetaConfig => {
  if (ROUTE_META[pathname]) {
    return {
      ...ROUTE_META[pathname],
      ogImage: ROUTE_META[pathname].ogImage || getDefaultOgImage(),
    };
  }

  const prefixMatch = Object.keys(ROUTE_META)
    .filter((key) => key !== "/" && pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];

  if (prefixMatch) {
    const meta = ROUTE_META[prefixMatch];
    return { ...meta, ogImage: meta.ogImage || getDefaultOgImage() };
  }

  return { ...FALLBACK_META, ogImage: getDefaultOgImage() };
};
